import {compact, toBase64, fromBase64} from './util.js'

let forceFragment = true
let nullFragment = '!'

class Url {
  constructor(data) {
    const defaults = {
      protocol: 'http',
      port: 80,
      path: '',
      params: {},
      hashPath: '',
      hashParams: {}
    }

    this.data = {...defaults, ...compact(data)}
  }

  static setForceFragment(activate=true, suffix='!') {
    forceFragment = activate
    nullFragment = suffix
  }


  // getters

  protocol() {
    return this.data['protocol']
  }

  host() {
    return this.data['host']
  }

  port() {
    return this.data['port']
  }

  path() {
    return this.data['path']
  }

  params() {
    return this.data['params']
  }

  hashPath() {
    return this.data['hashPath']
  }

  hash() {
    return this.data['hash']
  }

  hashParams({decode=true, parseIntList=null} = {}) {
    const result = {...this.data['hashParams']}

    if (decode) {
      for (const [k, v] of Object.entries(result)) {
        result[k] = decodeURIComponent(result[k])
      }
    }

    if (parseIntList) {
      for (const key of parseIntList) {
        if (result.hasOwnProperty(key)) {
          result[key] = parseInt(result[key])
        }
      }
    }

    return result
  }

  pack() {
    const data = this.params()[Url.packParam]

    if (data) {
      return JSON.parse(fromBase64(data))
    } else {
      return null
    }
  }

  hashPack() {
    const data = this.hashParams()[Url.packParam]

    if (data) {
      return JSON.parse(fromBase64(data))
    } else {
      return null
    }
  }


  // setters

  setProtocol(value) {
    this.data['protocol'] = value
    return this
  }

  setHost(value) {
    this.data['host'] = value
    return this
  }

  setPort(value) {
    this.data['port'] = value
    return this
  }

  setPath(value) {
    this.data['path'] = value
    return this
  }

  setParams(value) {
    this.data['params'] = value
    return this
  }

  setHashPath(value) {
    this.data['hashPath'] = value
    return this
  }

  setHashParams(value) {
    this.data['hashParams'] = value
    return this
  }

  updateParams(values) {
    for (const [k, v] of Object.entries(values)) {
      if (v === null) {
        delete this.data['params'][k]
      } else {
        this.data['params'][k] = v
      }
    }

    return this
  }

  updateHashParams(values) {
    for (const [k, v] of Object.entries(values)) {
      if (v === null) {
        delete this.data['hashParams'][k]
      } else {
        this.data['hashParams'][k] = v
      }
    }

    return this
  }

  setPack(data) {
    if (data === null || data === undefined) {
      delete this.data['params'][Url.packParam]
    } else {
      this.data['params'][Url.packParam] = toBase64(JSON.stringify(data))
    }

    return this
  }

  setHashPack(data) {
    if (data === null || data === undefined) {
      delete this.data['hashParams'][Url.packParam]
    } else {
      this.data['hashParams'][Url.packParam] = toBase64(JSON.stringify(data))
    }

    return this
  }

  apply(replace=false) {
    if (typeof window === 'undefined') {return undefined}
    if (document.location.href === this.url()) {return this}

    if (replace) {
      document.location.replace(this.url())
    } else {
      document.location.href = this.url()
    }

    return this
  }

  replaceState() {
    if (typeof window === 'undefined') {return undefined}

    history.replaceState(null, '', this.url())

    return this
  }

  pushState() {
    if (typeof window === 'undefined') {return undefined}

    history.pushState(null, '', this.url())

    return this
  }


  // to string

  escape(str) {
    return encodeURIComponent(str)
  }

  formatPort() {
    const isHttp80 = (this.data['protocol'] === 'http' && this.data['port'] === 80)
    const isHttps443 = (this.data['protocol'] === 'https' && this.data['port'] === 443)

    return (isHttp80 || isHttps443) ? '' : `:${this.data['port']}`
  }

  origin() {
    if (!this.data['protocol'] || !this.data['host'] || !this.data['port']) {
      return ''
    }

    return `${this.data['protocol']}://${this.data['host']}${this.formatPort()}`
  }

  formatQuery(params) {
    if (Object.keys(params).length === 0) {return ''}

    let pairs = []
    for (const [k, v] of Object.entries(params)) {
      const values = (Array.isArray(v) ? v : [v])
      for (const v of values) {
        pairs.push(`${k}=${v}`)
      }
    }

    return '?' + pairs.join('&')
  }

  formatHash() {
    if (this.hashPath() === '' && Object.keys(this.hashParams()).length === 0) {
      return forceFragment ? `#${nullFragment}` : ''
    }

    const query = this.formatQuery(this.hashParams())
    return `#${this.hashPath()}${query}`
  }

  resource() {
    const query = this.formatQuery(this.params())
    return `${this.path()}${query}${this.formatHash()}`
  }

  url() {
    return `${this.origin()}${this.resource()}`
  }


  // parse

  static parseQuery(string) {
    if (!string) {return {}}

    let result = {}
    for (const pair of string.split('&')) {
      const [k, v] = pair.split('=').map(e => decodeURIComponent(e))

      if (k.substr(-2) == '[]') {
        result[k] = result[k] || []
        result[k].push(v)
      } else {
        result[k] = v
      }
    }
    return result
  }

  static portFor(protocol) {
    return {
      'http': 80,
      'https': 443,
      'mysql': 3306
    }[protocol]
  }

  static parse(string) {
    const [nohash, hash] = string.split('#')
    const [noquery, query] = nohash.split('?')
    const [protocol, noproto] = noquery.split('://')
    const [nopath, path] = noproto.match(/^([^\/]+)(.*)$/).slice(1)
    const [host, port] = nopath.split(':')

    const [hashPath, hashQuery] = (hash ? hash.split('?') : [])

    const params = this.parseQuery(query)
    const hashParams = this.parseQuery(hashQuery)

    return new Url({
      protocol: protocol,
      host: host,
      port: (port ? parseInt(port) : this.portFor(protocol)),
      path: path,
      params: params,
      hash: hash,
      hashPath: hashPath == nullFragment ? '' : hashPath,
      hashParams: hashParams
    })
  }

  static current() {
    if (typeof window === 'undefined') {return undefined}

    return Url.parse(window.document.location.href)
  }
}

Url.packParam = 'p'

export default Url
